/**
 * Live Coach Feed — right-column panel on the Journal day view.
 *
 * Persistent surface that shows the Coach Engine's current read on the
 * trading day, organized top-down by temporal scope:
 *
 *   - DAILY (day scope) — the day's grade + ranked day-level observations.
 *     Powered by `coach.reviewDay`. This merges what used to live inside
 *     the standalone Daily Review modal: mistake clusters, setup spotlights,
 *     ATM performance, time-of-day windows, overtrading flags, duration
 *     profile, baseline vs historic average, and news/event impact.
 *
 *   - GROUPS (session scope) — session observations from sessions on the
 *     selected date. These update silently whenever a tracked session's
 *     coach runs (signature-based auto-refresh in JournalView). Newest first.
 *
 *   - 30D TRENDS (progress scope) — progress observations anchored at today's
 *     trading day. Top ~5 cross-session signals — "trending up," "resolved,"
 *     "biggest playbook leak," etc.
 *
 * Designed for the Discord vision of "Opt-In Live on Group Trades today,
 * Opt-In Full-Time Live in Today's Trades Overall tomorrow."
 *
 * Props:
 *   sessions           Full sessions array
 *   playbooks          Playbook library (for progress scope naming)
 *   selectedDate       'YYYY-MM-DD' — which day the DAILY + GROUPS sections filter to
 *   currentTradingDay  'YYYY-MM-DD' — anchors the 30d trends window
 *   dayTrades          Trades already filtered to selectedDate (for day scope)
 *   allTrades          Full trade history (for day-scope baselines)
 *   journalEntries     Full journal-entry dictionary keyed by date
 *   accounts           User's account list (for cluster labeling)
 *   getTradeNetPnL     (trade, account) => { netPnl }
 *   getAccountById     (accountId) => account | null
 *   userTier           'free' | 'pro' — gates advanced day analyzers
 *   onJumpToSession    (sessionId) => void — parent handles scroll+highlight
 *   onShowDailyReview  (date) => void — opens the full AI Daily Review modal.
 *                      Wired into each DAILY card so clicking a card routes the
 *                      user to the richer, modal-sized breakdown of the same
 *                      observation set (the Live Coach Feed summary is
 *                      deliberately compact; the modal is where depth lives).
 *   darkMode           bool
 */

import { useMemo, useEffect, useState, useRef } from 'react';
import { Sparkles, Activity, TrendingUp, ChevronRight, Calendar, Info, Lock, Brain, CalendarDays, FlaskConical, Eye, Target, WifiOff } from 'lucide-react';
import { coach, SEVERITY_STYLE } from '../../services/coach';
import { PATTERN_CATALOG } from '../../services/barAnalysis/setupPatterns';
import { STATUS as FRESHNESS_STATUS } from '../../services/barAnalysis/barFreshness';
import CoachAboutModal from './CoachAboutModal';
import CoachHealthScore from './CoachHealthScore';
import { setupStatusTier } from './setupStatusTier';
import { useCounterBiasWhisper } from '../../hooks/useCounterBiasWhisper';
import { useLivePatternStats } from '../../hooks/useLivePatternStats';
import {
  useWatchedSymbols,
  useAlignmentTimeframes,
  MAX_WATCHED,
  ALLOWED_ALIGNMENT_TFS,
} from '../../hooks/useWatchedSymbols';
import { useTimeframeAlignment } from '../../hooks/useTimeframeAlignment';
import { STANCE as TF_STANCE } from '../../services/barAnalysis/timeframeAlignment';

// localStorage key for the first-time onboarding auto-open. Once the user has
// seen the About modal at least once, we stop auto-opening it.
const ONBOARDING_SEEN_KEY = 'journifi.coach.onboardingSeen';

// localStorage key for the "your first insight is live" welcome callout. We
// track this separately from the About modal so the two onboarding moments
// don't collide — the About modal happens BEFORE the user has data, and this
// callout happens the FIRST TIME they have real data.
const FIRST_INSIGHT_SEEN_KEY = 'journifi.coach.firstInsightSeen';

// ────────────────────────────────────────────────────────────────────────────
// FeedItem — single observation row
// ────────────────────────────────────────────────────────────────────────────

// Letter-grade color mapping for the process sub-grade grid (Wave B.1).
// Matches the existing day-grade color palette so a `B` in the sub-grade
// chip looks the same as a `B` in the day grade badge.
const SUB_GRADE_COLORS = {
  A: { bg: 'bg-green-500/15',  text: 'text-green-300',  border: 'border-green-500/40',  textLight: 'text-green-700',  bgLight: 'bg-green-100',  borderLight: 'border-green-300' },
  B: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/40', textLight: 'text-emerald-700', bgLight: 'bg-emerald-100', borderLight: 'border-emerald-300' },
  C: { bg: 'bg-amber-500/15',   text: 'text-amber-300',   border: 'border-amber-500/40',   textLight: 'text-amber-700',   bgLight: 'bg-amber-100',   borderLight: 'border-amber-300' },
  D: { bg: 'bg-orange-500/15',  text: 'text-orange-300',  border: 'border-orange-500/40',  textLight: 'text-orange-700',  bgLight: 'bg-orange-100',   borderLight: 'border-orange-300' },
  F: { bg: 'bg-red-500/15',     text: 'text-red-300',     border: 'border-red-500/40',     textLight: 'text-red-700',     bgLight: 'bg-red-100',      borderLight: 'border-red-300' },
};

// 4-up grid renderer for dayProcessSubgrades observations (Wave B.1).
// Each sub-grade shows: short label + big letter + score detail on hover.
// Null sub-grades render as "—" (unevaluable) so the grid is always 4 cells.
const ProcessSubgradeGrid = ({ subGrades, darkMode }) => {
  if (!subGrades || typeof subGrades !== 'object') return null;
  const ORDER = [
    ['sizing',   'Sizing'],
    ['patience', 'Patience'],
    ['exit',     'Exit'],
    ['plan',     'Plan'],
  ];
  return (
    <div className="grid grid-cols-4 gap-1.5 mt-2">
      {ORDER.map(([key, label]) => {
        const sg = subGrades[key];
        if (!sg) {
          // Unevaluable — null cell with muted styling
          return (
            <div
              key={key}
              className={`flex flex-col items-center justify-center rounded px-2 py-1.5 border ${
                darkMode ? 'bg-gray-800/40 border-gray-700/40 text-gray-600' : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}
              title={`${label}: not enough data to grade`}
            >
              <span className="text-[9px] uppercase tracking-wider leading-none mb-1">{label}</span>
              <span className="text-base font-bold leading-none">—</span>
            </div>
          );
        }
        const colors = SUB_GRADE_COLORS[sg.letter] || SUB_GRADE_COLORS.C;
        const bg = darkMode ? colors.bg : colors.bgLight;
        const text = darkMode ? colors.text : colors.textLight;
        const border = darkMode ? colors.border : colors.borderLight;
        return (
          <div
            key={key}
            className={`flex flex-col items-center justify-center rounded px-2 py-1.5 border ${bg} ${border}`}
            title={`${label}: ${sg.detail}`}
          >
            <span className={`text-[9px] uppercase tracking-wider leading-none mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {label}
            </span>
            <span className={`text-base font-bold leading-none ${text}`}>{sg.letter}</span>
          </div>
        );
      })}
    </div>
  );
};

const FeedItem = ({ obs, context, onClick, darkMode }) => {
  const style = SEVERITY_STYLE[obs.severity] || SEVERITY_STYLE.info;
  const clickable = typeof onClick === 'function';

  // Wave B.1 — process sub-grades get a special 4-up grid renderer
  // between detail and action. Other observations render unchanged.
  const isSubgrades = obs.analyzer === 'dayProcessSubgrades' && obs.evidence?.subGrades;

  return (
    <div
      onClick={clickable ? onClick : undefined}
      className={`
        group relative rounded-md border-l-2 ${style.border}
        ${darkMode ? 'bg-gray-800/50 hover:bg-gray-800/80' : 'bg-gray-50 hover:bg-gray-100'}
        p-2.5 transition-colors
        ${clickable ? 'cursor-pointer' : ''}
      `}
    >
      {/* Context line: dot + source label + optional chevron */}
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shrink-0`} />
        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
          {context}
        </span>
        {clickable && (
          <ChevronRight
            size={12}
            className={`ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
          />
        )}
      </div>

      {/* Title */}
      <p className={`text-sm font-medium leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {obs.title}
      </p>

      {/* Sub-grade grid (only on dayProcessSubgrades observations) */}
      {isSubgrades && (
        <ProcessSubgradeGrid subGrades={obs.evidence.subGrades} darkMode={darkMode} />
      )}

      {/* Detail */}
      {obs.detail && (
        <p className={`text-xs leading-snug mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {obs.detail}
        </p>
      )}

      {/* Action */}
      {obs.action && (
        <p className={`text-[11px] italic leading-snug mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          → {obs.action}
        </p>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Empty-state blocks
// ────────────────────────────────────────────────────────────────────────────

// ─── Empty-state preview cards ──────────────────────────────────────────────
// New users haven't traded yet, so there are no observations to show. Rather
// than a dead "no data" placeholder, each empty state previews the *shape* of
// a real insight the Coach will eventually produce for them. The copy is
// grounded in the actual analyzer wording so what the user sees in onboarding
// matches what they'll see in the wild. Muted styling (opacity + dashed ring)
// keeps the preview visually obviously non-live so users don't mistake it for
// real data.

const SamplePreview = ({ darkMode, borderColor, dotColor, context, title, detail }) => (
  <div className={`rounded-md border-l-2 ${borderColor} ${darkMode ? 'bg-gray-800/40' : 'bg-gray-50/70'} p-2.5 opacity-70`}>
    <div className="flex items-center gap-1.5 mb-1">
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} />
      <span className={`text-[10px] uppercase tracking-wider italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {context}
      </span>
    </div>
    <p className={`text-xs font-medium leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
      {title}
    </p>
    <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
      {detail}
    </p>
  </div>
);

const EmptyDay = ({ darkMode }) => (
  <div className="space-y-2">
    <div className={`rounded-md border border-dashed p-3 text-center ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
      <Calendar size={14} className="mx-auto mb-1 opacity-60" />
      <p className="text-xs leading-snug">
        No trades yet today. The Coach will grade the day as trades land.
      </p>
    </div>
    {/* Preview what a real DAILY card will look like once the user journals */}
    <div className={`text-[10px] uppercase tracking-wider italic px-1 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
      Preview
    </div>
    <SamplePreview
      darkMode={darkMode}
      borderColor="border-green-500"
      dotColor="bg-green-500"
      context="Day"
      title="Top-tier day (A)"
      detail="65% WR · 1.8R avg · PF 2.4 · +$420."
    />
    <SamplePreview
      darkMode={darkMode}
      borderColor="border-amber-500"
      dotColor="bg-amber-500"
      context="Day · Discipline"
      title="“Revenge trade” dominated today's mistakes"
      detail="4× across 6 of 10 trades. Cost today: $180."
    />
  </div>
);

const EmptyToday = ({ darkMode }) => (
  <div className="space-y-2">
    <div className={`rounded-md border border-dashed p-3 text-center ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
      <Sparkles size={14} className="mx-auto mb-1 opacity-60" />
      <p className="text-xs leading-snug">
        Click the <span className="inline-block px-1 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px]">✨</span> on a group of trades to start live coaching.
      </p>
    </div>
    <div className={`text-[10px] uppercase tracking-wider italic px-1 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
      Preview
    </div>
    <SamplePreview
      darkMode={darkMode}
      borderColor="border-blue-500"
      dotColor="bg-blue-500"
      context="Morning Reversal"
      title="Thesis confirmed — 3/4 validated"
      detail="VWAP + Volume combo matched the pre-market read."
    />
  </div>
);

const EmptyTrends = ({ darkMode }) => (
  <div className="space-y-2">
    <div className={`rounded-md border border-dashed p-3 text-center ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
      <TrendingUp size={14} className="mx-auto mb-1 opacity-60" />
      <p className="text-xs leading-snug">
        Trends unlock after ~1 week of coached sessions.
      </p>
    </div>
    <div className={`text-[10px] uppercase tracking-wider italic px-1 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
      Preview
    </div>
    <SamplePreview
      darkMode={darkMode}
      borderColor="border-green-500"
      dotColor="bg-green-500"
      context="Cross-session"
      title="“Late entries” resolved"
      detail="Down 70% frequency over the last 14 days — pattern broken."
    />
    <SamplePreview
      darkMode={darkMode}
      borderColor="border-amber-500"
      dotColor="bg-amber-500"
      context="Cross-session"
      title="Breakout setups leaking"
      detail="−$520 across 8 sessions — WR dropped from 58% to 32%."
    />
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// ProTeaser — inline upsell card shown at the end of each section when the
// user is on the Free tier. Clicking opens the About modal so the upsell flow
// is centralized in one place (no separate upgrade modal).
// ────────────────────────────────────────────────────────────────────────────

const ProTeaser = ({ message, onClick, darkMode }) => (
  <button
    type="button"
    onClick={onClick}
    title="See what Pro unlocks"
    className={`
      w-full flex items-center gap-2 px-3 py-2 rounded-md border border-dashed
      text-left transition-all group
      ${darkMode
        ? 'bg-purple-500/[0.03] border-purple-400/20 hover:bg-purple-500/[0.08] hover:border-purple-400/40'
        : 'bg-purple-50/50 border-purple-300/40 hover:bg-purple-50 hover:border-purple-400/60'
      }
    `}
  >
    <Lock size={11} className={`shrink-0 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
    <span className={`text-[11px] flex-1 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
      {message}
    </span>
    <span className={`text-[10px] font-semibold shrink-0 flex items-center gap-0.5 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
      Pro
      <ChevronRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
    </span>
  </button>
);

// ────────────────────────────────────────────────────────────────────────────
// FirstInsightCallout — one-time celebration banner on the user's VERY FIRST
// real Coach observation. Renders inside the DAILY section at the top, above
// the actual observation cards. Dismisses permanently via the × button (stores
// the dismiss timestamp in localStorage). We decide "first time" by reading
// the FIRST_INSIGHT_SEEN_KEY inside the hosting component's useEffect — this
// component is purely a dumb renderer.
// ────────────────────────────────────────────────────────────────────────────

const FirstInsightCallout = ({ darkMode, onDismiss }) => (
  <div className={`relative rounded-lg border p-3 mb-2 ${
    darkMode
      ? 'bg-gradient-to-br from-purple-500/10 via-purple-500/[0.05] to-transparent border-purple-400/30'
      : 'bg-gradient-to-br from-purple-50 via-white to-white border-purple-200'
  }`}>
    <div className="flex items-start gap-2">
      <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
        darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
      }`}>
        <Sparkles size={14} />
      </div>
      <div className="min-w-0 flex-1 pr-4">
        <p className={`text-xs font-semibold ${darkMode ? 'text-purple-200' : 'text-purple-900'}`}>
          Your first insight just landed
        </p>
        <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Click any card below to open the full Daily Review. The Coach gets sharper as you log more days.
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss welcome banner"
        title="Got it"
        className={`absolute top-1.5 right-1.5 p-0.5 rounded-md transition-colors ${
          darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
      >
        {/* Inline × glyph — lucide X would add an unused import */}
        <span aria-hidden className="block w-3 h-3 leading-none text-[11px] font-bold">×</span>
      </button>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// FreshnessPill — compact "Latest 13:45 · 8m ago" indicator with a status
// dot. Used on both live cards so the user can see at a glance whether the
// underlying bar data is current. Without this, a stale feed silently makes
// the cards look authoritative when they're actually showing 30+ min-old data.
// ────────────────────────────────────────────────────────────────────────────

const FreshnessPill = ({ freshness, darkMode }) => {
  if (!freshness || freshness.status === FRESHNESS_STATUS.EMPTY) return null;

  const tone = (() => {
    switch (freshness.status) {
      case FRESHNESS_STATUS.FRESH:
        return darkMode
          ? { dot: 'bg-emerald-400', text: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-400/30' }
          : { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50',     border: 'border-emerald-300' };
      case FRESHNESS_STATUS.STALE:
        return darkMode
          ? { dot: 'bg-amber-400',   text: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-400/30' }
          : { dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',       border: 'border-amber-300' };
      case FRESHNESS_STATUS.VERY_STALE:
      case FRESHNESS_STATUS.DOWN:
      default:
        return darkMode
          ? { dot: 'bg-rose-400',    text: 'text-rose-300',    bg: 'bg-rose-500/10',    border: 'border-rose-400/30' }
          : { dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',        border: 'border-rose-300' };
    }
  })();

  const tooltip = `Latest bar: ${freshness.barTimeLabel} (${freshness.ageLabel}). ` +
    `Status: ${freshness.status}. ` +
    `Stats reflect ${freshness.barCount} bars. ` +
    `If this lags, the bar feed is behind — check JournifiSync NT.`;

  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[9px] border ${tone.bg} ${tone.border} ${tone.text}`}
    >
      <span className={`inline-block w-1 h-1 rounded-full ${tone.dot}`} />
      <span className="font-semibold tabular-nums">{freshness.barTimeLabel}</span>
      <span className="opacity-75">·</span>
      <span className="tabular-nums">{freshness.ageLabel}</span>
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// FeedDownCard — replaces the live cards entirely when bar freshness is
// 'down' (> 60 min stale during the active trading day). Better to show one
// loud "feed down" message than two cards with possibly-wrong stats.
// ────────────────────────────────────────────────────────────────────────────

const FeedDownCard = ({ freshness, symbol, darkMode }) => (
  <div className={`relative rounded-lg border p-3 mb-2 ${
    darkMode
      ? 'bg-gradient-to-br from-rose-500/10 via-rose-500/[0.05] to-transparent border-rose-400/30'
      : 'bg-gradient-to-br from-rose-50 via-white to-white border-rose-200'
  }`}>
    <div className="flex items-start gap-2">
      <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
        darkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-700'
      }`}>
        <WifiOff size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-[10px] uppercase tracking-wider font-semibold ${
          darkMode ? 'text-rose-300' : 'text-rose-700'
        }`}>
          Bar feed appears down
        </p>
        <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Latest {symbol || ''} 5m bar is from <span className="font-semibold">{freshness.barTimeLabel}</span> ({freshness.ageLabel}).
          Live setup + probability cards are hidden until bars catch up.
        </p>
        <p className={`text-[10px] mt-1 italic ${darkMode ? 'text-rose-200/70' : 'text-rose-700/80'}`}>
          Check the JournifiSync NT window — is the Bars feed still Running? Did your data feed (Bulenox, NinjaTrader) disconnect?
        </p>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// SetupStatusCard — passive empty-state surface explaining WHY no setups are
// firing. Replaces the bare "No setups firing" placeholder so the trader can
// tell the difference between "engine is running but waiting on more bars"
// vs "engine is fully armed but the bar was quiet."
//
// Lives in the same slot as LivePatternStatsCard / CounterBiasWhisperCard
// when both of those have nothing to show. Dashed border + neutral tones so
// it reads as "ambient status," not "alert."
//
// Tier classification lives in `./setupStatusTier.js` (extracted so it's
// testable from .mjs without the React/JSX import chain).
// ────────────────────────────────────────────────────────────────────────────

const SetupStatusCard = ({ freshness, symbol, darkMode, compact = false }) => {
  const tier = setupStatusTier(freshness);
  // The "all armed" tier reads as a positive ambient state (everything's
  // watching), so use the Activity heartbeat icon. Warming-up tiers use the
  // same icon for visual consistency — the title carries the meaning.
  return (
    <div className={`rounded-lg border border-dashed p-3 mb-2 ${
      darkMode ? 'border-gray-700/50 bg-gray-800/20' : 'border-gray-300 bg-gray-50/60'
    }`}>
      <div className="flex items-start gap-2">
        <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
          darkMode ? 'bg-gray-700/40 text-gray-400' : 'bg-gray-100 text-gray-500'
        }`}>
          <Activity size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {tier.title}
            </span>
            {symbol && (
              <span
                className={`text-[10px] font-mono font-semibold px-1.5 py-px rounded ${
                  darkMode ? 'bg-white/10 text-gray-100 border border-white/15' : 'bg-gray-900/5 text-gray-800 border border-gray-300'
                }`}
                title={`Analyzing ${symbol} bars`}
              >
                {symbol}
              </span>
            )}
            <FreshnessPill freshness={freshness} darkMode={darkMode} />
          </div>
          <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {tier.detail}
          </p>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// StaleBanner — slim banner rendered INSIDE a live card when freshness is
// 'stale' or 'very-stale' but not 'down'. Surfaces "you can still read this
// but bars are X min behind" without yanking the card away.
// ────────────────────────────────────────────────────────────────────────────

const StaleBanner = ({ freshness, darkMode }) => {
  const isVery = freshness.status === FRESHNESS_STATUS.VERY_STALE;
  const tone = isVery
    ? (darkMode
        ? 'bg-rose-500/10 border-rose-400/30 text-rose-200'
        : 'bg-rose-50 border-rose-200 text-rose-800')
    : (darkMode
        ? 'bg-amber-500/10 border-amber-400/30 text-amber-200'
        : 'bg-amber-50 border-amber-200 text-amber-800');
  return (
    <div className={`text-[10px] leading-snug rounded border px-2 py-1 mb-2 ${tone}`}>
      <span className="font-semibold">
        {isVery ? 'Data may be stale.' : 'Heads up:'}
      </span>{' '}
      Latest bar is {freshness.ageLabel}. Stats below reflect bars through {freshness.barTimeLabel}.
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// CounterBiasWhisperCard — surfaces opposite-side setups during cognitive
// anchoring (Wave F). Pain point: user woke up bullish, watched for a long
// off the 15M 21EMA bounce, missed a clean Golden Crayon rejection short.
// The bias was actively filtering out the real setup. This card is the
// "just awareness" surface — not judgment, not "you missed this."
// Amber gradient so it reads as "pay attention" without being a warning.
// ────────────────────────────────────────────────────────────────────────────

const CounterBiasWhisperCard = ({ whisper, freshness, darkMode, onDismiss, symbol }) => {
  if (!whisper) return null;
  const oppositeUpper = whisper.oppositeDirection?.toUpperCase() || '';
  const biasUpper = whisper.bias?.toUpperCase() || '';
  const isStaleOrVery = freshness && (
    freshness.status === FRESHNESS_STATUS.STALE ||
    freshness.status === FRESHNESS_STATUS.VERY_STALE
  );
  return (
    <div className={`relative rounded-lg border p-3 mb-2 ${
      darkMode
        ? 'bg-gradient-to-br from-amber-500/10 via-amber-500/[0.05] to-transparent border-amber-400/30'
        : 'bg-gradient-to-br from-amber-50 via-white to-white border-amber-200'
    }`}>
      {isStaleOrVery && <StaleBanner freshness={freshness} darkMode={darkMode} />}
      <div className="flex items-start gap-2">
        <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
          darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
        }`}>
          <Eye size={14} />
        </div>
        <div className="min-w-0 flex-1 pr-4">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${
              darkMode ? 'text-amber-300' : 'text-amber-700'
            }`}>
              Counter-Bias Whisper
            </span>
            {symbol && (
              // Symbol badge — answers "which ticker is this firing on?"
              // Critical signal for multi-instrument traders; even single-symbol
              // traders benefit from explicit context (no need to infer from
              // "well, I trade MNQ, so it must be MNQ").
              <span
                className={`text-[10px] font-mono font-semibold px-1.5 py-px rounded ${
                  darkMode ? 'bg-white/10 text-gray-100 border border-white/15' : 'bg-gray-900/5 text-gray-800 border border-gray-300'
                }`}
                title={`Analyzing ${symbol} bars`}
              >
                {symbol}
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-px rounded ${
              darkMode ? 'bg-amber-500/15 text-amber-200' : 'bg-amber-100 text-amber-800'
            }`}>
              {oppositeUpper} vs {biasUpper}
            </span>
            <FreshnessPill freshness={freshness} darkMode={darkMode} />
          </div>
          <p className={`text-xs font-semibold leading-snug ${darkMode ? 'text-amber-100' : 'text-amber-900'}`}>
            {whisper.title}
          </p>
          <p className={`text-[11px] mt-1 leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {whisper.detail}
          </p>
          {whisper.action && (
            <p className={`text-[11px] mt-1.5 leading-snug italic ${darkMode ? 'text-amber-200/80' : 'text-amber-800/90'}`}>
              {whisper.action}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss counter-bias whisper"
          title="Dismiss"
          className={`absolute top-1.5 right-1.5 p-0.5 rounded-md transition-colors ${
            darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <span aria-hidden className="block w-3 h-3 leading-none text-[11px] font-bold">×</span>
        </button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// LivePatternStatsCard — surfaces the actionable patterns firing on the most
// recent closed 5m bar, each annotated with today's historical follow-through
// probability (Wave G.2 → G.3). Answers the trader's "what's the probability
// we bounce the 5M 09EMA after this fires?" question directly.
//
// Renders nothing when no actionable patterns are currently firing — passive
// surface, no nagging.
// ────────────────────────────────────────────────────────────────────────────

// One outcome row inside the card: "Closes in direction (30 min) — 67% (n=12)"
const StatsRow = ({ label, sample, probability, darkMode }) => {
  if (!sample || sample === 0) {
    return (
      <div className="flex items-center justify-between text-[10px] leading-snug">
        <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{label}</span>
        <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
      </div>
    );
  }
  // Color the probability green ≥66%, amber 40-65%, red <40%
  const pct = Math.round(probability * 100);
  const tone =
    pct >= 66
      ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
      : pct >= 40
        ? (darkMode ? 'text-amber-300'   : 'text-amber-700')
        : (darkMode ? 'text-rose-300'    : 'text-rose-700');
  return (
    <div className="flex items-center justify-between text-[10px] leading-snug">
      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{label}</span>
      <span>
        <span className={`font-semibold ${tone}`}>{pct}%</span>
        <span className={`ml-1 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>n={sample}</span>
      </span>
    </div>
  );
};

// One pattern block inside the card: header + ordered stat rows.
// `compact` mode strips the watchFor line to fit two cards side-by-side
// when the user is watching 2 tickers — preserves the essential signal
// (label + direction + stats) without overflowing the narrower column.
const PatternBlock = ({ fire, bucketStats, darkMode, compact = false }) => {
  const catEntry = PATTERN_CATALOG[fire.type] || {};
  const label = catEntry.label || fire.type;
  const timeframe = catEntry.timeframe || null;
  // Direction-specific watchFor (v7.5.11). Patterns whose follow-through
  // depends on direction (wolf-stack, holy-grail, 3-bar-play) expose
  // watchForLong + watchForShort overrides. PatternBlock picks the right
  // one based on the fire's direction; falls back to the generic `watchFor`
  // for patterns that read the same regardless of direction (signal-bar,
  // turtle-soup, uno-reverse) or that are inherently single-direction
  // (h2 = always long, l2 = always short).
  const dirLower = (fire.direction || '').toLowerCase();
  const watchFor =
    (dirLower === 'long'  && catEntry.watchForLong)  ? catEntry.watchForLong  :
    (dirLower === 'short' && catEntry.watchForShort) ? catEntry.watchForShort :
    (catEntry.watchFor || null);
  const dirUpper = fire.direction.toUpperCase();
  const dirTone = fire.direction === 'long'
    ? (darkMode ? 'text-emerald-300 bg-emerald-500/15' : 'text-emerald-700 bg-emerald-100')
    : (darkMode ? 'text-rose-300 bg-rose-500/15' : 'text-rose-700 bg-rose-100');
  return (
    <div className={`rounded border px-2 py-1.5 ${
      darkMode ? 'bg-gray-800/40 border-gray-700/40' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${
          darkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>{label}</span>
        <span className={`text-[9px] px-1 py-px rounded ${dirTone}`}>{dirUpper}</span>
        {timeframe && (
          // Which chart TF the pattern was detected on. Important for
          // multi-TF traders: "Holy Grail · LONG · 5m" tells the trader
          // to confirm on the 5m chart, not whatever's currently open.
          <span
            className={`text-[9px] font-mono px-1 py-px rounded ${
              darkMode ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-gray-100 text-gray-600 border border-gray-300'
            }`}
            title={`Pattern detected on the ${timeframe} bar`}
          >
            {timeframe}
          </span>
        )}
      </div>
      {bucketStats ? (
        <div className="space-y-0.5">
          {Object.entries(bucketStats.outcomes).map(([name, o]) => (
            <StatsRow
              key={name}
              label={DEFAULT_OUTCOME_LABELS[name] || name}
              sample={o.sample}
              probability={o.probability}
              darkMode={darkMode}
            />
          ))}
        </div>
      ) : (
        <div className={`text-[10px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          First fire today — no stats yet
        </div>
      )}
      {!compact && watchFor && (
        // Actionable post-fire guidance — the "now what?" answer. Drops
        // to a smaller, italicized line below the stats so it doesn't
        // crowd the numbers but is right there when the trader needs it.
        <div className={`text-[10px] italic mt-1 leading-snug ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>↳ Watch for: </span>
          {watchFor}
        </div>
      )}
    </div>
  );
};

// Label map for outcome keys → human-readable display
const DEFAULT_OUTCOME_LABELS = {
  followsThrough3:  'Follow-through (15m)',
  followsThrough6:  'Follow-through (30m)',
  followsThrough12: 'Follow-through (60m)',
  tagsEma9_6:       'Tags 9 EMA (30m)',
  tagsEma21_6:      'Tags 21 EMA (30m)',
};

const LivePatternStatsCard = ({ activeFires, stats, freshness, darkMode, symbol }) => {
  if (!Array.isArray(activeFires) || activeFires.length === 0) return null;
  const isStaleOrVery = freshness && (
    freshness.status === FRESHNESS_STATUS.STALE ||
    freshness.status === FRESHNESS_STATUS.VERY_STALE
  );

  return (
    <div className={`relative rounded-lg border p-3 mb-2 ${
      darkMode
        ? 'bg-gradient-to-br from-emerald-500/10 via-emerald-500/[0.05] to-transparent border-emerald-400/30'
        : 'bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-200'
    }`}>
      {isStaleOrVery && <StaleBanner freshness={freshness} darkMode={darkMode} />}
      <div className="flex items-start gap-2 mb-2">
        <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
          darkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
        }`}>
          <Target size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className={`text-[10px] uppercase tracking-wider font-semibold ${
              darkMode ? 'text-emerald-300' : 'text-emerald-700'
            }`}>
              Setups firing now
            </p>
            {symbol && (
              // Symbol badge — see CounterBiasWhisperCard for rationale.
              // Without this, the card silently assumes "you know what you're
              // trading"; that assumption breaks the moment a user watches
              // multiple instruments or wants to monitor a different symbol.
              <span
                className={`text-[10px] font-mono font-semibold px-1.5 py-px rounded ${
                  darkMode ? 'bg-white/10 text-gray-100 border border-white/15' : 'bg-gray-900/5 text-gray-800 border border-gray-300'
                }`}
                title={`Analyzing ${symbol} bars`}
              >
                {symbol}
              </span>
            )}
            <FreshnessPill freshness={freshness} darkMode={darkMode} />
          </div>
          <p className={`text-[11px] leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            On the latest 5m bar. Probabilities are today&apos;s history.
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        {activeFires.map((fire) => {
          const bucketKey = `${fire.type}:${fire.direction}`;
          const bucketStats = stats?.results?.[bucketKey] || null;
          return (
            <PatternBlock
              key={bucketKey}
              fire={fire}
              bucketStats={bucketStats}
              darkMode={darkMode}
            />
          );
        })}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────────────────

/**
 * Sort observations newest-first using the session's aiInsight.generatedAt
 * where available. Falls back to priority for deterministic order when
 * timestamps are missing or equal.
 */
const sortNewestFirst = (observations) => {
  return [...observations].sort((a, b) => {
    const ta = a.generatedAt ? Date.parse(a.generatedAt) : 0;
    const tb = b.generatedAt ? Date.parse(b.generatedAt) : 0;
    if (tb !== ta) return tb - ta;
    return (b.priority || 0) - (a.priority || 0);
  });
};

// ────────────────────────────────────────────────────────────────────────────
// TimeframeAlignmentPills (v7.5.7) — top-of-panel macro stance row.
// Three colored pills (default 15M/1H/4H), each showing the trend stance
// of that timeframe right now. Click a pill to swap its timeframe.
//
// Colors:
//   🟢 Green   — Bullish (EMA stack up + price above shortest)
//   🔴 Red     — Bearish (EMA stack down + price below shortest)
//   🟡 Yellow  — Mixed (anything in between — chop/transition)
//   ⚪ Gray    — Unknown (bars unavailable or not enough warmup)
//
// Rendered once per panel; the polling hook lives inside LiveSymbolPanel
// so multi-symbol panels don't share alignment state.
// ────────────────────────────────────────────────────────────────────────────
const TF_STANCE_TONE = (status, darkMode) => {
  if (status === TF_STANCE.BULLISH) {
    return darkMode
      ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-200'
      : 'bg-emerald-100 border-emerald-400 text-emerald-800';
  }
  if (status === TF_STANCE.BEARISH) {
    return darkMode
      ? 'bg-rose-500/20 border-rose-400/50 text-rose-200'
      : 'bg-rose-100 border-rose-400 text-rose-800';
  }
  if (status === TF_STANCE.MIXED) {
    return darkMode
      ? 'bg-amber-500/20 border-amber-400/50 text-amber-200'
      : 'bg-amber-100 border-amber-400 text-amber-800';
  }
  // UNKNOWN
  return darkMode
    ? 'bg-gray-700/30 border-gray-600/40 text-gray-500'
    : 'bg-gray-100 border-gray-300 text-gray-500';
};

const TF_STANCE_LABEL = {
  [TF_STANCE.BULLISH]: 'Bullish — EMA stack up, price above shortest EMA',
  [TF_STANCE.BEARISH]: 'Bearish — EMA stack down, price below shortest EMA',
  [TF_STANCE.MIXED]:   'Mixed — EMAs crossing or price in chop',
  [TF_STANCE.UNKNOWN]: 'Unknown — bars unavailable or not enough warmup',
};

const TimeframeAlignmentPills = ({
  alignment,
  timeframes,
  onSwapTimeframe,
  darkMode,
}) => {
  // Index of pill whose swap dropdown is currently open; null = none.
  const [openIdx, setOpenIdx] = useState(null);

  // Build a status lookup so we can render pills in the user's slot order
  // even when the hook returns results in a different order.
  const statusByTf = useMemo(() => {
    const m = new Map();
    for (const a of (alignment || [])) m.set(a.timeframe, a.status);
    return m;
  }, [alignment]);

  const slots = (timeframes || []).slice(0, 3);
  if (slots.length === 0) return null;

  return (
    <div className="flex items-center gap-1 mb-1.5">
      <span className={`text-[9px] uppercase tracking-wider mr-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        TF:
      </span>
      {slots.map((tf, idx) => {
        const status = statusByTf.get(tf) || TF_STANCE.UNKNOWN;
        const isOpen = openIdx === idx;
        const tone = TF_STANCE_TONE(status, darkMode);
        return (
          <div key={`${tf}-${idx}`} className="relative">
            <button
              type="button"
              onClick={() => setOpenIdx(o => (o === idx ? null : idx))}
              className={`px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded border ${tone}`}
              title={`${tf.toUpperCase()} — ${TF_STANCE_LABEL[status] || status}. Click to swap timeframe.`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              {tf.toUpperCase()}
            </button>
            {isOpen && (
              <div
                className={`absolute z-50 mt-1 left-0 min-w-[80px] rounded-md border shadow-lg ${
                  darkMode ? 'bg-gray-900/95 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
                }`}
                role="listbox"
              >
                {ALLOWED_ALIGNMENT_TFS.map(opt => {
                  const isCurrent = opt === tf;
                  const isInOtherSlot = slots.includes(opt) && !isCurrent;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={isInOtherSlot}
                      onClick={() => {
                        if (isInOtherSlot) return;
                        onSwapTimeframe?.(idx, opt);
                        setOpenIdx(null);
                      }}
                      className={`w-full text-left text-[11px] font-mono px-3 py-1 ${
                        isCurrent
                          ? (darkMode ? 'bg-white/10 font-semibold' : 'bg-gray-100 font-semibold')
                          : isInOtherSlot
                            ? (darkMode ? 'opacity-30 cursor-not-allowed' : 'opacity-30 cursor-not-allowed')
                            : (darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50')
                      }`}
                      title={isInOtherSlot ? `${opt.toUpperCase()} is already in another slot` : undefined}
                    >
                      {opt.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// LiveSymbolPanel — one symbol's worth of live coach surfaces (whisper +
// firing-now stats + per-symbol feed-down detection). Extracted from
// LiveCoachFeed so the parent can render N of these for the multi-symbol
// watchlist (max 2). Each panel owns its own polling state — two panels
// watching MNQ + MES poll independently but share the bar cache, so the
// disk I/O cost is the same as a single panel.
//
// `compact` mode reduces visual weight when 2 panels render side-by-side:
//   - Pattern blocks drop the watchFor line
//   - Card headers shrink slightly
// ────────────────────────────────────────────────────────────────────────────
const LiveSymbolPanel = ({
  symbol,
  selectedDate,
  currentTradingDay,
  journalEntry,
  recentDir,
  darkMode,
  compact = false,
  alignmentTimeframes,
  onSwapAlignmentTimeframe,
}) => {
  // Per-symbol multi-TF stance — fetches each TF's bars, classifies, polls
  // every 60s. Shared bar cache means MNQ + MES side-by-side don't double
  // the I/O when both watch the same timeframes.
  const { alignment } = useTimeframeAlignment({
    symbol,
    timeframes: alignmentTimeframes,
    currentTradingDay,
  });
  const {
    whisper: counterBiasWhisper,
    dismiss: dismissCounterBiasWhisper,
    freshness: whisperFreshness,
  } = useCounterBiasWhisper({
    journalEntry,
    symbol,
    tradingDay: selectedDate,
    currentTradingDay,
    recentTradeDirection: recentDir,
  });

  const {
    activeFires: livePatternFires,
    stats: livePatternStats,
    freshness: statsFreshness,
  } = useLivePatternStats({
    symbol,
    tradingDay: selectedDate,
    currentTradingDay,
  });

  // Both hooks poll the same bars (shared cache). Either may be null if
  // its surface didn't activate (no thesis → no whisper; no patterns → no
  // stats). Prefer statsFreshness as the primary signal since it polls
  // unconditionally when on the current trading day.
  const panelFreshness = statsFreshness || whisperFreshness || null;
  const isFeedDown = panelFreshness?.status === FRESHNESS_STATUS.DOWN;

  // Pills row — rendered at the top of every panel state (feed-down,
  // empty, with-content). The pills provide macro context independent
  // of whether anything is firing on the 5m bar; that's their whole
  // point. Always show them so they aren't surprising-by-absence.
  const pills = (
    <TimeframeAlignmentPills
      alignment={alignment}
      timeframes={alignmentTimeframes}
      onSwapTimeframe={onSwapAlignmentTimeframe}
      darkMode={darkMode}
    />
  );

  if (isFeedDown) {
    return (
      <div>
        {pills}
        <FeedDownCard
          freshness={panelFreshness}
          symbol={symbol}
          darkMode={darkMode}
        />
      </div>
    );
  }

  // If neither surface has anything to show, render the SetupStatusCard so
  // the trader can see the engine is alive — it's either warming up (not
  // enough bars yet) or fully armed but on a quiet bar. Without this the
  // panel would look dead at early-session ("totally empty under setups
  // firing" — Chris, 2026-05-26).
  const hasContent = counterBiasWhisper || (Array.isArray(livePatternFires) && livePatternFires.length > 0);
  if (!hasContent) {
    return (
      <div>
        {pills}
        <SetupStatusCard
          freshness={panelFreshness}
          symbol={symbol}
          darkMode={darkMode}
          compact={compact}
        />
      </div>
    );
  }

  return (
    <div>
      {pills}
      {counterBiasWhisper && (
        <CounterBiasWhisperCard
          whisper={counterBiasWhisper}
          freshness={whisperFreshness}
          darkMode={darkMode}
          onDismiss={() => dismissCounterBiasWhisper()}
          symbol={symbol}
        />
      )}
      <LivePatternStatsCard
        activeFires={livePatternFires}
        stats={livePatternStats}
        freshness={statsFreshness}
        darkMode={darkMode}
        symbol={symbol}
        compact={compact}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// WatchedSymbolsSelector — inline chip-based selector for the Live Coach
// header. Each selected ticker renders as a removable chip; an "add" chip
// opens a dropdown of traded symbols. At MAX_WATCHED (2), the add chip
// hides. Selecting nothing returns to AUTO mode (follow latest trade).
// ────────────────────────────────────────────────────────────────────────────
const WatchedSymbolsSelector = ({
  watchedSymbols,
  tradedSymbols,
  autoSymbol,
  onAdd,
  onRemove,
  onClear,
  darkMode,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const isAuto = watchedSymbols.length === 0;
  const canAdd = watchedSymbols.length < MAX_WATCHED;

  // Symbols available to add: traded symbols not already in the watched list.
  // Fall back to FUTURES_SPECS top picks if the user has no trade history
  // yet — better than an empty dropdown on first launch.
  const addableSymbols = (tradedSymbols || [])
    .filter(s => !watchedSymbols.includes(s));

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Watching:
      </span>
      {isAuto ? (
        // AUTO mode chip — shows the resolved symbol with an "auto" hint.
        // Clicking it opens the picker so the user can pin an explicit choice.
        <button
          type="button"
          onClick={() => setAddOpen(o => !o)}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono rounded border ${
            darkMode
              ? 'bg-white/5 border-white/15 text-gray-200 hover:bg-white/10'
              : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
          }`}
          title={`Auto mode — following latest trade${autoSymbol ? ` (${autoSymbol})` : ''}. Click to pin explicit symbols.`}
        >
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>auto:</span>
          <span className="font-semibold">{autoSymbol || '—'}</span>
          <span aria-hidden className="ml-0.5 text-[9px]">▾</span>
        </button>
      ) : (
        watchedSymbols.map(sym => (
          <span
            key={sym}
            className={`inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 text-[10px] font-mono font-semibold rounded border ${
              darkMode
                ? 'bg-purple-500/15 border-purple-400/40 text-purple-100'
                : 'bg-purple-100 border-purple-300 text-purple-800'
            }`}
          >
            {sym}
            <button
              type="button"
              onClick={() => onRemove(sym)}
              aria-label={`Stop watching ${sym}`}
              title={`Stop watching ${sym}`}
              className={`px-1 rounded hover:bg-white/10 leading-none ${
                darkMode ? 'text-purple-300/70' : 'text-purple-700/80'
              }`}
            >
              ×
            </button>
          </span>
        ))
      )}
      {canAdd && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setAddOpen(o => !o)}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${
              darkMode
                ? 'bg-white/5 border-white/15 text-gray-300 hover:bg-white/10 hover:text-white'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
            title="Add a symbol to watch"
          >
            + add
          </button>
          {addOpen && (
            <div
              className={`absolute z-50 mt-1 right-0 min-w-[120px] rounded-md border shadow-lg ${
                darkMode ? 'bg-gray-900/95 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
              }`}
            >
              {addableSymbols.length === 0 ? (
                <p className={`px-3 py-2 text-[11px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No more symbols available
                </p>
              ) : (
                <>
                  {addableSymbols.map(sym => (
                    <button
                      key={sym}
                      type="button"
                      onClick={() => { onAdd(sym); setAddOpen(false); }}
                      className={`w-full text-left text-[11px] font-mono px-3 py-1.5 ${
                        darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                      }`}
                    >
                      {sym}
                    </button>
                  ))}
                  {!isAuto && (
                    <>
                      <div className={`border-t my-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                      <button
                        type="button"
                        onClick={() => { onClear(); setAddOpen(false); }}
                        className={`w-full text-left text-[10px] italic px-3 py-1.5 ${
                          darkMode ? 'text-gray-400 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Stop pinning specific symbols — follow latest trade instead"
                      >
                        ↺ back to auto mode
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LiveCoachFeed = ({
  sessions = [],
  playbooks = [],
  selectedDate,
  currentTradingDay,
  dayTrades = [],
  allTrades = [],
  journalEntries = {},
  accounts = [],
  getTradeNetPnL,
  getAccountById,
  userTier = 'free',
  // Phase 17 — ATM template version history keyed by ATM name. Passed
  // through to coach.reviewDay so the cross-ATM atmParamDirection
  // analyzer can scan param-change-direction patterns across templates.
  // Optional — undefined silently disables that analyzer.
  ntTemplateVersions = {},
  onJumpToSession,
  onShowDailyReview,      // (date) => void — opens the AI Daily Review modal for the full breakdown
  onSaveDayInsight,       // (insightBlob) => void — persists to journal entry for selectedDate
  onOpenLicenseSettings,  // () => void — opens Settings modal on the License tab (routed from the Upgrade button)
  onOpenCoachDashboard,   // () => void — opens the persistent Coach Dashboard (Phase 2 mental model)
  onOpenWeeklyDigest,     // () => void — opens the Weekly Coach Digest modal
  onOpenWhatIfBacktest,   // () => void — opens the What-If Backtest modal (Phase 14)
  coachHealth = null,     // optional: pre-computed health-score object from computeHealthScore (Phase 14)
  maxHeight,              // optional number (px) — lock panel height to parent's left column (grid row match)
  darkMode = true
}) => {
  // Historical = any date that isn't today's trading day. Past-day insights are
  // snapshots: compute once, persist, and lock to memory until explicitly cleared.
  const isHistorical = !!(
    selectedDate && currentTradingDay && selectedDate !== currentTradingDay
  );

  const savedDayInsight = journalEntries?.[selectedDate]?.aiDayInsight || null;

  // Today's journal entry as a stable reference — the live `coach.reviewDay`
  // path needs it, but the historical path doesn't, so we hoist it out of
  // the memo body so its identity can drive deps directly.
  const journalEntry = journalEntries?.[selectedDate] || null;

  // ── DAILY (day scope) ──────────────────────────────────────────────────
  // For today: always compute live (cheap, reflects latest trades).
  // For past days: prefer the saved snapshot; fall back to a fresh compute
  // (then persist below via effect) so pre-existing entries get seeded lazily.
  //
  // Dep strategy:
  //   - Historical path (snapshot): only `savedDayInsight` identity matters.
  //     We conditionally null the live-path deps so editing an unrelated
  //     journal field doesn't churn the memo (which would create a new
  //     wrapper object and cascade re-renders downstream).
  //   - Live path: full deps (dayTrades, journalEntries, etc.) — needed
  //     by the analyzers and unavoidable for accurate fresh signals.
  //   - Returns the savedDayInsight directly (no wrapper) so consumers see
  //     stable identity until the snapshot is re-saved.
  // Live-path deps — nulled when historical so unrelated journal edits
  // (plan, notes, eventTags on other days) don't force a snapshot re-wrap.
  // Extracted to local vars so the useMemo dep array is statically analyzable
  // (avoids the "complex expression in dependency array" lint warning).
  const liveDayTrades       = isHistorical ? null : dayTrades;
  const liveAllTrades       = isHistorical ? null : allTrades;
  const liveJournalEntry    = isHistorical ? null : journalEntry;
  const liveJournalEntries  = isHistorical ? null : journalEntries;
  const liveAccounts        = isHistorical ? null : accounts;
  const liveGetTradeNetPnL  = isHistorical ? null : getTradeNetPnL;
  const liveGetAccountById  = isHistorical ? null : getAccountById;

  const dayReview = useMemo(() => {
    if (!selectedDate) return { observations: [], grade: null, day: null };

    if (isHistorical && savedDayInsight && Array.isArray(savedDayInsight.observations)) {
      return savedDayInsight;
    }

    return coach.reviewDay(selectedDate, {
      trades: dayTrades,
      allTrades,
      journalEntry: journalEntry || {},
      allJournalEntries: journalEntries,
      accounts,
      getTradeNetPnL,
      getAccountById,
      tier: userTier,
      top: 6,
      // Phase 17 — enables atmParamDirection cross-ATM analyzer
      ntTemplateVersions,
    });
    // dayTrades, allTrades, etc. are referenced via the live*-prefixed
    // closures below; eslint can't see through the `isHistorical ? null : X`
    // indirection in the body, so we list both forms for completeness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate, isHistorical, savedDayInsight,
    liveDayTrades, liveAllTrades, liveJournalEntry, liveJournalEntries,
    liveAccounts, liveGetTradeNetPnL, liveGetAccountById,
    userTier
  ]);

  // Seed the snapshot on first view of a historical day. Idempotent — only
  // writes when we computed a fresh review AND there's no saved insight yet.
  // Strict-Mode guard: track which date we already seeded in a ref so the
  // dev-mode double-effect doesn't write twice (each write produces a new
  // savedAt string, so React.StrictMode would otherwise write two slightly
  // different snapshots for the same view).
  const seededDateRef = useRef(null);
  useEffect(() => {
    if (!isHistorical) return;
    if (savedDayInsight) return;                     // already frozen
    if (seededDateRef.current === selectedDate) return; // already seeded this date
    if (typeof onSaveDayInsight !== 'function') return;
    if (!dayReview || !Array.isArray(dayReview.observations) || dayReview.observations.length === 0) return;
    seededDateRef.current = selectedDate;
    onSaveDayInsight({
      observations: dayReview.observations,
      grade: dayReview.grade || null,
      day: dayReview.day || null,
      savedAt: new Date().toISOString()
    });
  }, [isHistorical, selectedDate, savedDayInsight, dayReview, onSaveDayInsight]);

  // Today section: flatten observations from sessions on the selected date.
  // Each observation carries generatedAt + sessionId + sessionName so the
  // feed item can render context + sort reliably.
  const todayObservations = useMemo(() => {
    if (!selectedDate) return [];
    const rows = [];
    sessions.forEach(session => {
      if (session.date !== selectedDate) return;
      const obs = session.aiInsight?.observations;
      if (!Array.isArray(obs) || obs.length === 0) return;
      obs.forEach(o => rows.push({
        ...o,
        sessionId: session.id,
        sessionName: session.name || 'Group',
        generatedAt: session.aiInsight?.generatedAt || null,
        autoTrack: !!session.aiInsight?.autoTrack
      }));
    });
    return sortNewestFirst(rows);
  }, [sessions, selectedDate]);

  // Trends: progress scope anchored at currentTradingDay, 30d window.
  // `allTrades` enables cross-day bar-context patterns (sharp-entry hour
  // clustering, MFE-leakage hour clustering) on trades whose `barAnalysis`
  // has been populated via the backfill or per-trade chart visits.
  const trendObservations = useMemo(() => {
    return coach.analyze(
      { type: 'progress', windowDays: 30 },
      { sessions, playbooks, currentTradingDay, allTrades }
    ).slice(0, 5);
  }, [sessions, playbooks, currentTradingDay, allTrades]);

  // Count of today's sessions currently auto-tracking (for live badge)
  const liveSessionCount = useMemo(() => {
    if (!selectedDate) return 0;
    return sessions.filter(s => s.date === selectedDate && s.aiInsight?.autoTrack).length;
  }, [sessions, selectedDate]);

  const grade = dayReview.grade;
  const hasDayTrades = dayTrades && dayTrades.length > 0;

  // ── Counter-Bias Whisper (Wave F) ───────────────────────────────────────
  // Surfaces opposite-side setups during cognitive anchoring. Active only on
  // the current trading day, only when the journal entry has a thesis with
  // clear directional intent, and only when an actionable opposite-direction
  // Wave C pattern fires on the most recent 5m bar.
  //
  // Symbol resolution: prefer the symbol the trader has been ACTIVE in.
  //   1. Most recent trade today (live context — they're already in this name)
  //   2. Most recent trade overall (their typical instrument as fallback)
  //   3. No symbol → hook short-circuits and skips polling
  //
  // Recent direction: the direction of the most recent trade today, used to
  // suppress the whisper if they've already entered in the counter direction
  // (they clearly already noticed the setup and acted).
  const { recentSymbol, recentDir } = useMemo(() => {
    const pickLatest = (trades) => {
      if (!Array.isArray(trades) || trades.length === 0) return null;
      // dayTrades / allTrades aren't guaranteed sorted by entryTime — find the
      // max by string sort on entryTime (HH:MM ET is lex-sortable).
      let best = null;
      for (const t of trades) {
        if (!t?.symbol) continue;
        if (!best) { best = t; continue; }
        const a = (t.entryTime || '');
        const b = (best.entryTime || '');
        if (a > b) best = t;
      }
      return best;
    };
    const latestToday = pickLatest(dayTrades);
    if (latestToday) {
      return { recentSymbol: latestToday.symbol, recentDir: latestToday.direction || null };
    }
    const latestEver = pickLatest(allTrades);
    return {
      recentSymbol: latestEver?.symbol || null,
      recentDir: null,  // intentionally null — only today's trade direction triggers the dedup
    };
  }, [dayTrades, allTrades]);

  // ── Watched symbols (v7.5.7) ───────────────────────────────────────────
  // Multi-ticker watchlist (max 2). Sources:
  //   - User explicit pin via the inline selector → useWatchedSymbols hook
  //   - Fallback to recentSymbol (auto mode = follow latest trade)
  // Each effective symbol drives its own <LiveSymbolPanel> instance, which
  // owns its own polling state + freshness + dismiss state.
  const {
    watchedSymbols,
    addSymbol,
    removeSymbol,
    clear: clearWatchedSymbols,
    isAuto: isAutoMode,
  } = useWatchedSymbols();

  // Multi-TF alignment pill timeframes (v7.5.7). Shared across panels —
  // both watched symbols use the same TF trio. User can click any pill
  // to swap that slot's timeframe; persists across reloads.
  const { timeframes: alignmentTimeframes, setTimeframeAt } = useAlignmentTimeframes();

  const tradedSymbols = useMemo(() => {
    const seen = new Set();
    const lists = [dayTrades, allTrades];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const t of list) {
        if (t?.symbol) seen.add(t.symbol);
      }
    }
    return Array.from(seen).sort();
  }, [dayTrades, allTrades]);

  const effectiveSymbols = useMemo(() => {
    if (watchedSymbols.length > 0) return watchedSymbols;
    return recentSymbol ? [recentSymbol] : [];
  }, [watchedSymbols, recentSymbol]);

  // Compact = side-by-side rendering. Triggers when 2 panels active so
  // each panel sheds the watchFor line + shrinks slightly to fit half the
  // Coach column width.
  const isMultiPanel = effectiveSymbols.length > 1;

  // ── About / Onboarding modal state ─────────────────────────────────────
  // Visible when the user clicks the tier badge, OR auto-opened once on
  // first-ever view so new users learn what the AI Coach does. The "seen"
  // flag is written when the user actually dismisses the modal — not when
  // it auto-opens — so a user who closes the app before reading anything
  // still gets to see the explainer next time.
  const [aboutOpen, setAboutOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const seen = window.localStorage.getItem(ONBOARDING_SEEN_KEY);
      if (!seen) {
        setAboutOpen(true);
      }
    } catch {
      // localStorage can throw in private-mode / disabled storage; silently skip.
    }
  }, []);

  // Persist the "seen" flag on first dismissal. Used by both handleCloseAbout
  // and handleUpgrade (which closes the modal as part of routing to settings).
  const markAboutSeen = () => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ONBOARDING_SEEN_KEY, new Date().toISOString());
    } catch { /* localStorage unavailable — accept that the modal may auto-open again */ }
  };

  // ── First-Insight celebration callout ───────────────────────────────────
  // Renders a one-time "your first insight just landed" banner inside the
  // DAILY section when the user sees their very first real Coach observation.
  // Separate from the About modal because the two moments happen at different
  // times (About = empty-hand first launch; First Insight = first real data).
  const [showFirstInsight, setShowFirstInsight] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (dayReview.observations.length === 0) return;
    try {
      const seen = window.localStorage.getItem(FIRST_INSIGHT_SEEN_KEY);
      if (!seen) setShowFirstInsight(true);
    } catch {
      // localStorage unavailable → skip; callout stays false.
    }
  }, [dayReview.observations.length]);

  const dismissFirstInsight = () => {
    setShowFirstInsight(false);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(FIRST_INSIGHT_SEEN_KEY, new Date().toISOString());
      } catch {
        // silent
      }
    }
  };

  const handleOpenAbout = () => setAboutOpen(true);
  const handleCloseAbout = () => {
    markAboutSeen();
    setAboutOpen(false);
  };
  const handleUpgrade = () => {
    // Route to Settings → License. During tester phase, Pro is unlocked by
    // redeeming a code; after public launch this is where the Stripe/paywall
    // flow will land. If the route-up callback isn't wired, fall back to a
    // lightweight alert so the user isn't staring at a dead button.
    if (typeof onOpenLicenseSettings === 'function') {
      onOpenLicenseSettings();
    } else if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert('Open Settings → License to redeem a Pro code.');
    }
    markAboutSeen();
    setAboutOpen(false);
  };

  return (
    <>
    <div
      className={`
        rounded-lg border flex flex-col
        sticky top-2
        max-h-[calc(100vh-120px)]
        ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}
      `}
      style={
        // When the parent measures its left-column height (lg+), pin our
        // max-height to that value AND cap at the viewport so a tall left
        // column (e.g., many news events + long plan) still fits on screen.
        // CSS `min()` takes the smaller of the two constraints. Inline styles
        // win over the Tailwind class above, so this effectively overrides
        // the viewport-only cap when a measurement is present.
        typeof maxHeight === 'number' && maxHeight > 0
          ? { maxHeight: `min(${Math.round(maxHeight)}px, calc(100vh - 120px))` }
          : undefined
      }
    >
      {/* Header — stays pinned as body scrolls */}
      <div className={`
        flex items-center justify-between px-4 py-3
        rounded-t-lg border-b
        ${darkMode ? 'border-gray-600 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}
      `}>
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={16} className="text-purple-400 shrink-0" />
          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            AI Coach
          </h3>
          {/* Day grade badge, when there are trades */}
          {hasDayTrades && grade && grade.letter && grade.letter !== '—' && (
            <span
              className={`text-[11px] font-black px-1.5 py-0.5 rounded ${grade.color} ${darkMode ? 'bg-gray-900/60' : 'bg-white'} shrink-0`}
              title={`Day Grade (score ${grade.score})`}
            >
              {grade.letter}
            </span>
          )}
          {liveSessionCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 shrink-0">
              <Activity size={9} className="animate-pulse" />
              {liveSessionCount} live
            </span>
          )}
        </div>
        {/* Coach Health Score (Phase 14) — compact pill showing the
            current 0-100 score + grade + trend arrow. Only renders when
            the host computes + supplies the health object (so existing
            callers that don't yet pass it get an unchanged header). */}
        {coachHealth && (
          <div className="mr-1 shrink-0">
            <CoachHealthScore health={coachHealth} compact darkMode={darkMode} />
          </div>
        )}
        {/* Dashboard trigger — opens the persistent Coach mental model
            (Phase 2). Sits next to the tier badge so it's always one click
            away from anywhere the user is in the journal. Only renders
            when the parent supplies the callback (so the button is invisible
            when the dashboard isn't wired up — defensive default). */}
        {typeof onOpenCoachDashboard === 'function' && (
          <button
            type="button"
            onClick={onOpenCoachDashboard}
            title="Open Coach Dashboard — your persistent mental model"
            className={`text-[10px] shrink-0 mr-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full border backdrop-blur-sm transition-all ${
              darkMode
                ? 'bg-purple-500/15 border-purple-400/30 text-purple-200 hover:bg-purple-500/25 hover:border-purple-400/50'
                : 'bg-purple-100/70 border-purple-300/60 text-purple-700 hover:bg-purple-200/80 hover:border-purple-400'
            }`}
          >
            <Brain size={9} />
            Dashboard
          </button>
        )}
        {/* Weekly Digest trigger — opens the "Sunday-night recap" view.
            Distinct icon (calendar-days) so the user can tell it apart
            from the dashboard at a glance. */}
        {typeof onOpenWeeklyDigest === 'function' && (
          <button
            type="button"
            onClick={onOpenWeeklyDigest}
            title="Open Weekly Coach Digest — week-over-week recap"
            className={`text-[10px] shrink-0 mr-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full border backdrop-blur-sm transition-all ${
              darkMode
                ? 'bg-blue-500/15 border-blue-400/30 text-blue-200 hover:bg-blue-500/25 hover:border-blue-400/50'
                : 'bg-blue-100/70 border-blue-300/60 text-blue-700 hover:bg-blue-200/80 hover:border-blue-400'
            }`}
          >
            <CalendarDays size={9} />
            Weekly
          </button>
        )}
        {/* What-If Backtest trigger (Phase 14) — opens the hypothetical
            equity-curve replay. Amber accent to distinguish it from the
            blue Weekly + purple Dashboard buttons. */}
        {typeof onOpenWhatIfBacktest === 'function' && (
          <button
            type="button"
            onClick={onOpenWhatIfBacktest}
            title="Open What-If Backtest — hypothetical equity curve under your rules"
            className={`text-[10px] shrink-0 mr-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full border backdrop-blur-sm transition-all ${
              darkMode
                ? 'bg-amber-500/15 border-amber-400/30 text-amber-200 hover:bg-amber-500/25 hover:border-amber-400/50'
                : 'bg-amber-100/70 border-amber-300/60 text-amber-700 hover:bg-amber-200/80 hover:border-amber-400'
            }`}
          >
            <FlaskConical size={9} />
            What-If
          </button>
        )}
        <button
          type="button"
          onClick={handleOpenAbout}
          title="About the AI Coach — what it does, what Pro unlocks"
          className={`text-[10px] shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full border backdrop-blur-sm transition-all ${
            userTier === 'pro'
              ? (darkMode
                  ? 'bg-gradient-to-b from-purple-500/90 to-purple-600/90 border-purple-400/60 text-white shadow-sm shadow-purple-500/30 ring-1 ring-purple-400/30 hover:from-purple-400/90 hover:to-purple-500/90'
                  : 'bg-gradient-to-b from-purple-500/90 to-purple-600/90 border-purple-400 text-white shadow-sm hover:from-purple-400 hover:to-purple-500')
              : (darkMode
                  ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.09] hover:border-white/25 hover:text-white'
                  : 'bg-white/60 border-gray-300/60 text-gray-600 hover:bg-white hover:border-gray-400 hover:text-gray-900')
          }`}
        >
          <Info size={9} />
          {userTier === 'pro' ? 'Pro' : 'Free'}
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">

        {/* WATCHED SYMBOLS SELECTOR (v7.5.7) — lets the user pin up to 2
            tickers to watch. Empty = auto mode (follow latest trade). Lives
            here (top of body) instead of in the header bar because the
            header is already busy with the tier/dashboard/weekly buttons,
            and the selector belongs visually next to the panels it drives. */}
        <WatchedSymbolsSelector
          watchedSymbols={watchedSymbols}
          tradedSymbols={tradedSymbols}
          autoSymbol={isAutoMode ? recentSymbol : null}
          onAdd={addSymbol}
          onRemove={removeSymbol}
          onClear={clearWatchedSymbols}
          darkMode={darkMode}
        />

        {/* WATCHLIST PANELS — one per effective symbol. Auto mode = 1 panel
            (follow recent trade); explicit mode = 1 or 2 panels (user pinned).
            Each panel owns its own polling, freshness, dismiss state — so
            two symbols watched at once stay independent (one feed going down
            doesn't blank the other, dismissing one whisper doesn't dismiss
            the other). Side-by-side grid when 2 panels active. */}
        {effectiveSymbols.length === 0 ? (
          <div className={`rounded-lg border border-dashed px-3 py-6 text-center ${
            darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'
          }`}>
            <p className="text-[11px] italic">
              No symbol to watch — take a trade or click <span className="font-semibold">+ add</span> above to pin one.
            </p>
          </div>
        ) : (
          <div className={isMultiPanel ? 'grid grid-cols-2 gap-2' : ''}>
            {effectiveSymbols.map(sym => (
              <LiveSymbolPanel
                key={sym}
                symbol={sym}
                selectedDate={selectedDate}
                currentTradingDay={currentTradingDay}
                journalEntry={journalEntry}
                recentDir={sym === recentSymbol ? recentDir : null}
                darkMode={darkMode}
                compact={isMultiPanel}
                alignmentTimeframes={alignmentTimeframes}
                onSwapAlignmentTimeframe={setTimeframeAt}
              />
            ))}
          </div>
        )}

        {/* DAILY — day scope */}
        <section>
          <div className="flex items-center justify-between mb-2 px-1">
            <h4 className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Daily {dayReview.observations.length > 0 && `· ${dayReview.observations.length}`}
            </h4>
            {dayReview.observations.length > 0 && (
              isHistorical ? (
                <span
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider"
                  title={
                    dayReview.savedAt
                      ? `Saved snapshot · locked on ${new Date(dayReview.savedAt).toLocaleString()}`
                      : 'Saved snapshot — locked to memory for this past trading day'
                  }
                >
                  <span className={`inline-flex rounded-full h-1.5 w-1.5 ${darkMode ? 'bg-blue-400' : 'bg-blue-500'}`} />
                  <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>Saved</span>
                </span>
              ) : (
                <span
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider"
                  title="Live — re-grading as trades land today"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                  </span>
                  <span className={darkMode ? 'text-green-400' : 'text-green-600'}>Live</span>
                </span>
              )
            )}
          </div>
          {!hasDayTrades || dayReview.observations.length === 0 ? (
            <EmptyDay darkMode={darkMode} />
          ) : (
            <div className="space-y-2">
              {/* First-time-ever welcome banner — dismisses on click + persists */}
              {showFirstInsight && (
                <FirstInsightCallout darkMode={darkMode} onDismiss={dismissFirstInsight} />
              )}
              {dayReview.observations.map((obs, idx) => (
                <FeedItem
                  key={`day:${obs.id || idx}`}
                  obs={obs}
                  context="Day"
                  onClick={
                    // Cards route to the full Daily Review modal, which is the
                    // richer surface for the same observation set. Falls back
                    // to null if no route-up handler is wired (keeps the panel
                    // usable in isolation — e.g., during component tests).
                    typeof onShowDailyReview === 'function' && selectedDate
                      ? () => onShowDailyReview(selectedDate)
                      : null
                  }
                  darkMode={darkMode}
                />
              ))}
              {userTier === 'free' && hasDayTrades && (
                <ProTeaser
                  message="Compare today vs your 30/90d baseline + see historical event citations"
                  onClick={handleOpenAbout}
                  darkMode={darkMode}
                />
              )}
            </div>
          )}
        </section>

        {/* GROUPS — session scope */}
        <section>
          <h4 className={`text-[10px] uppercase tracking-wider mb-2 px-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Groups {todayObservations.length > 0 && `· ${todayObservations.length}`}
          </h4>
          {todayObservations.length === 0 ? (
            <EmptyToday darkMode={darkMode} />
          ) : (
            <div className="space-y-2">
              {todayObservations.map((obs, idx) => (
                <FeedItem
                  key={`today:${obs.id || idx}`}
                  obs={obs}
                  context={obs.sessionName}
                  onClick={onJumpToSession ? () => onJumpToSession(obs.sessionId) : null}
                  darkMode={darkMode}
                />
              ))}
              {userTier === 'free' && (
                <ProTeaser
                  message="Cross-account comparison: spot when the same setup diverges across prop firms"
                  onClick={handleOpenAbout}
                  darkMode={darkMode}
                />
              )}
            </div>
          )}
        </section>

        {/* 30D TRENDS — progress scope */}
        <section>
          <h4 className={`text-[10px] uppercase tracking-wider mb-2 px-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            30d Trends {trendObservations.length > 0 && `· ${trendObservations.length}`}
          </h4>
          {trendObservations.length === 0 ? (
            <EmptyTrends darkMode={darkMode} />
          ) : (
            <div className="space-y-2">
              {trendObservations.map((obs, idx) => (
                <FeedItem
                  key={`trend:${obs.id || idx}`}
                  obs={obs}
                  context="Cross-session"
                  onClick={null}  // Trends are informational; no jump target
                  darkMode={darkMode}
                />
              ))}
              {userTier === 'free' && trendObservations.length > 0 && (
                <ProTeaser
                  message="Risk regime detection + deeper trend statistics across 90/180 days"
                  onClick={handleOpenAbout}
                  darkMode={darkMode}
                />
              )}
            </div>
          )}
        </section>

      </div>
    </div>

    {/* About / Onboarding modal — click the tier badge (or auto-open on first view) */}
    {aboutOpen && (
      <CoachAboutModal
        userTier={userTier}
        onClose={handleCloseAbout}
        onUpgrade={handleUpgrade}
        darkMode={darkMode}
      />
    )}
    </>
  );
};

export default LiveCoachFeed;
